import WPStagingCommon from './../wpstg.js';
import * as dom from './wpstg-dom-utils.js';
import wpstgAuthCheck from '../backup/modules/wpstg-auth-check';

/**
 * Represents a class for managing and displaying process and success modals in WPStaging.
 * This class provides methods to start, open, initialize, update, and stop process modals.
 *
 * @class
 */
export default class WpstgProcessModal {
  constructor(wpstgObject = wpstg) {
    this.wpstgObject = wpstgObject;
    this.action = '';
    this.isProcessCancelled = false;
    this.isProcessFinished = false;
    this.processTime = 0;
    this.processInterval = '';
    this.modal = null;
    this.disableCancelButton = false;
    this.title = '';
    this.showCancelButton = false;
    this.percentage = 0;
    this.cloneId = '';
    this.successModal = '';
    this.showProcessLogs = true;
    this.stagingSiteUrl = '';
  }

  /**
   * Starts the process, initializes intervals, and modifies UI elements as needed.
   *
   * @return {void}
   */
  start() {
    this.showCancelButton = true;
    this.processInterval = setInterval(() => {
      if (wpstgAuthCheck.isAuthenticated || this.action === 'wpstg_push_processing') {
        this.processTime++;
        this.updateElapsedTime();
      }
    }, 1000);
    if (this.action === 'wpstg_delete_clone') {
      dom.hide('.wpstg--modal--process--logs--tail');
      this.showCancelButton = false;
      const container = WPStagingCommon.getSwalContainer();
      const processPercentageElement = container.querySelector('.wpstg--modal--process--percent');
      processPercentageElement.textContent = 100;
    }

    if (this.action === 'wpstg_cloning' || this.action === 'wpstg_push_processing') {
      wpstgAuthCheck.status = true;
      wpstgAuthCheck.start();
    }

    dom.hide('.wpstg-prev-step-link, #wpstg-start-updating, .wpstg-loader, #wpstg-cancel-cloning, .wpstg-log-details, .wpstg-progress-bar, #wpstg-processing-header');
    dom.hide('.wpstg-processing-timer, #wpstg-cancel-pushing, .wpstg-progress-bar-wrapper, #wpstg-cancel-cloning-update, #wpstg-removing-clone');
  }

  /**
   * Stops the ongoing process, resets values, and clears intervals.
   *
   * @return {void}
   */
  stop() {
    this.processTime = 0;
    this.modal = null;
    this.percentage = '';
    this.resetCancelChecks();

    clearInterval(this.processInterval);
    WPStagingCommon.closeSwalModal();
    dom.loadingBar();

    dom.hide('#wpstg-try-again, #wpstg-home-link, #wpstg-show-log-button, .wpstg-loader');
    if (this.action === 'wpstg_cloning' || this.action === 'wpstg_push_processing') {
      wpstgAuthCheck.stop();
    }
  }

  resetCancelChecks() {
    const element = document.getElementById('wpstg-new-clone');
    if (element === null) {
      setTimeout(()=> {
        this.resetCancelChecks();
      }, 1000);
      return;
    }

    setTimeout(()=> {
      this.isProcessCancelled = false;
      WPStaging.isCancelled = false;
    }, 2000);
    dom.loadingBar('hidden');
  }

  /**
   * Cancels the ongoing process if confirmed by the user.
   *
   * @return {void}
   */
  cancelProcess() {
    if (confirm('Are you sure you want to cancel cloning process?')) {
      WPStaging.isCancelled = true;
      this.isProcessCancelled = true;
      if (this.action === 'wpstg_push_processing') {
        const container = WPStagingCommon.getSwalContainer();
        const processTitleElement = container.querySelector('.wpstg--modal--process--title');
        processTitleElement.textContent = 'Canceling Please wait....';
        this.stop();
        dom.show('#wpstg-workflow');
        WPStaging.messages = [];
      } else {
        this.cancelCloneAction();
      }
    }
  }

  /**
   * Sets the title and cancellation status based on the response job or status.
   *
   * @param {Object} response - The response object containing information about the process job and status.
   * @return {void}
   */
  setTitle(response) {
    this.title = response.job;
    if (response.job === 'database' || response.job === 'jobCopyDatabaseTmp') {
      this.title = 'Copying Database';
      this.disableCancelButton = false;
    } else if (response.job === 'SearchReplace') {
      this.title = 'Processing Data';
      this.disableCancelButton = false;
    } else if (response.job === 'directories' || response.job === 'jobFileScanning') {
      this.title = 'Scanning Files';
      this.disableCancelButton = false;
    } else if (response.job === 'files' || response.job === 'data' || response.job === 'jobCopy' || response.job === 'jobSearchReplace') {
      this.title = 'Copying Files';
      this.disableCancelButton = false;
    } else if (response.job === 'Backup') {
      this.title = 'Backup Files Scanning';
      this.disableCancelButton = false;
    } else if (response.job === 'finish' || response.status === 'finished') {
      this.title = 'Process Finished';
      this.disableCancelButton = false;
    } else if (response.job === 'jobDatabaseRename') {
      this.title = 'Renaming Database';
      this.disableCancelButton = true;
    } else if (response.job === 'jobData') {
      this.title = 'Updating Database Data';
      this.disableCancelButton = true;
    }
  }

  /**
   * Sets the stored percentage value if the provided parameter is not null.
   *
   * @param {Object} params - Parameters containing the percentage value.
   * @return {void}
   */
  setPercentage(params) {
    if (params.percentage !== null) {
      this.percentage = params.percentage;
    }
  }

  /**
   * Initializes the process modal with specific behavior and appearance configurations.
   *
   * @return {void}
   */
  initializeProcessModal() {
    const modal = document.querySelector('#wpstg--modal--backup--process');
    const html = modal.innerHTML;
    modal.parentNode.removeChild(modal);
    this.modal = {
      html: null,
      cancelBtnTxt: null,
      processTime: 0,
      instance: WPStagingCommon.getSwalModal(true, {
        content: 'wpstg--process--content',
      }).fire({
        html: html,
        cancelButtonText: 'Cancel',
        showCancelButton: this.showCancelButton,
        showConfirmButton: false,
        showCloseButton: false,
        showLoaderOnConfirm: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        onRender: () => {
          const cancelButton = WPStagingCommon.getSwalContainer().getElementsByClassName('wpstg--swal2-cancel wpstg--btn--cancel')[0];
          const btnCancel = cancelButton.cloneNode(true);
          cancelButton.parentNode.replaceChild(btnCancel, cancelButton);
          btnCancel.addEventListener('click', () => {
            this.cancelProcess();
          });
        },
      }),
    };
    this.start();
  }

  /**
   * Sets the content of the process modal based on the stored title, percentage, and cancellation status.
   *
   * @return {void}
   */
  setProcessModal() {
    const container = WPStagingCommon.getSwalContainer();
    const cancelButton = container.getElementsByClassName('wpstg--swal2-cancel wpstg--btn--cancel')[0];
    const processTitleElement = container.querySelector('.wpstg--modal--process--title');
    const processPercentageElement = container.querySelector('.wpstg--modal--process--percent');
    processTitleElement.textContent = this.title;
    processPercentageElement.textContent = this.percentage;
    cancelButton.disabled = this.disableCancelButton;
  }

  /**
   * Opens and initializes a process modal with the provided data and parameters.
   *
   * @param {Object|undefined} data - Data related to the process.
   * @param {Object} params - Parameters for configuring the process modal.
   * @param {boolean} [showLogs=false] - Whether to display logs in the modal.
   * @param {boolean} [showCancelButton=true] - Whether to show the cancel button in the modal.
   * @return {void}
   */
  openProcessModal(data, params, showLogs = false, showCancelButton = true) {
    if (!this.isProcessCancelled && !WPStaging.isCancelled) {
      if (data !== undefined) {
        this.action = data.action;
        this.cloneId = data.cloneID;
      }
      this.showCancelButton = showCancelButton;
      this.showLogs = showLogs;
      if (!this.modal) {
        this.initializeProcessModal();
      } else {
        if (params.job) {
          this.setTitle(params);
          this.setPercentage(params);
          this.setProcessModal();
          if (params.job === 'finish') {
            this.stop();
          }
        } else {
          if (params.status === 'finished') {
            this.stop();
          }
        }
      }
    }
  };

  /**
   * Formats the given time duration (in seconds) into a string representation (HH:mm:ss).
   *
   * @return {string} The formatted time duration string.
   */
  formatTimeDuration() {
    return new Date(this.processTime * 1000).toISOString().slice(11, 19) + 's';
  }

  /**
   * Updates the displayed elapsed time in the modal if a process interval is set.
   *
   * @return {void}
   */
  updateElapsedTime() {
    if (this.processInterval !== null) {
      const container = WPStagingCommon.getSwalContainer();
      const elapsedTimeElement = container.querySelector('.wpstg--modal--process--elapsed-time');
      elapsedTimeElement.textContent = this.formatTimeDuration();
    }
  };

  /**
   * Determines the corresponding cancellation action name based on the current action.
   *
   * @return {string|undefined} The cancellation action name or undefined if no match is found.
   */
  getCancelCloneAction() {
    const actions = [
      {
        name: 'wpstg_cloning',
        cancel: 'wpstg_cancel_clone',
      },
      {
        name: 'wpstg_reset',
        cancel: 'wpstg_cancel_update',
      },
      {
        name: 'wpstg_update',
        cancel: 'wpstg_cancel_update',
      },
      {
        name: 'wpstg_push_processing',
        cancel: 'wpstg_cancel_push_processing',
      },
    ];
    if (this.action !== '') {
      const result = actions.find((action) => action.name === this.action);
      return result.cancel;
    }
  }

  /**
   * Cancels the ongoing clone action if there is one, using a fetch request.
   * If the cancellation is successful, it stops the action and optionally reloads the page.
   * If there are further actions or the cancellation is not successful, it retries the cancellation process recursively.
   *
   * @return {void}
   */
  cancelCloneAction() {
    if (this.action) {
      fetch(this.wpstgObject.ajaxUrl, {
        method: 'POST',
        credentials: 'same-origin',
        body: new URLSearchParams({
          action: this.getCancelCloneAction(),
          accessToken: this.wpstgObject.accessToken,
          nonce: this.wpstgObject.nonce,
          clone: this.cloneId,
        }),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }).then((response) => {
        if (response.ok) {
          return response.json();
        }
        return Promise.reject(response);
      }).then((response) => {
        if (response && 'undefined' !== typeof (response.delete) && response.delete === 'finished' || response === true) {
          this.stop();
          WPStaging.loadOverview();
          WPStaging.messages = [];
        }
        if (response !== true) {
          this.cancelCloneAction();
        }
      }).catch((error) => {
        console.log(this.wpstgObject.i18n['somethingWentWrong'], error);
      });
    }
  }

  /**
   * Initializes the success modal with the provided parameters and displays it.
   *
   * @param {Object} params - The parameters for configuring the success modal content.
   * @return {void}
   */
  initializeSuccessModal(params) {
    this.successModal = this.setSuccessModalContent(params);
    WPStagingCommon.getSwalModal(true, {
      confirmButton: 'wpstg--btn--confirm wpstg-green-button wpstg-button wpstg-link-btn wpstg-100-width',
    }).fire({
      'icon': 'success',
      'html': this.successModal.innerHTML,
      'confirmButtonText': 'Close',
      'showCancelButton': false,
      'showConfirmButton': true,
      'allowOutsideClick': false,
    }).then((result)=> {
      if (result.value) {
        WPStagingCommon.closeSwalModal();
        dom.hide('.wpstg-loader');
        WPStaging.messages = [];
        if (this.action === 'wpstg_push_processing') {
          location.reload();
        }
        this.action = '';
      }
    });
    if (this.showProcessLogs) {
      dom.show('.wpstg--modal--download--logs--wrapper');
    }
    dom.hide('.wpstg-loader');
    if (this.action !== 'wpstg_delete_clone') {
      WPStaging.loadOverview();
    }
  }

  /**
   * Sets the content of the success modal based on the provided parameters.
   *
   * @param {Object} params - The parameters for configuring the modal content.
   * @param {string|null} params.title - The title to be displayed in the modal.
   * @param {string|null|undefined} params.body - The body content of the modal.
   * @returns {HTMLElement} The modified modal element.
   */

  setSuccessModalContent(params) {
    const modal = document.getElementById('wpstg--modal--backup--download');
    if (params.title !== null) {
      modal.innerHTML = modal.innerHTML.replace('{title}', params.title);
    }
    if (this.showProcessLogs) {
      modal.innerHTML = modal.innerHTML.replace('{btnTxtLog}', '<span style="text-decoration: underline">Show Logs</span>');
    }
    if (params.body !== null && typeof params.body !== 'undefined') {
      let messageBody = params.body;
      if (this.stagingSiteUrl !== null) {
        messageBody += '<br><strong><a href="'+this.stagingSiteUrl+'" target="_blank" id="wpstg-clone-url">'+this.stagingSiteUrl+'</a></strong>';
      }

      if (this.action === 'wpstg_push_processing') {
        messageBody += '<a href="https://wp-staging.com/refresh-and-clear-cache/" target="_blank">How to delete the cache.</a>';
        const downloadModalTextElement = modal.querySelector('.wpstg-download-modal-text');
        if (downloadModalTextElement) {
          downloadModalTextElement.classList.add('wpstg--red');
        }
      }

      if (this.action === 'wpstg_cloning') {
        const wpstgRateUsElement = modal.querySelector('.wpstg-rate-us');
        if (wpstgRateUsElement) {
          wpstgRateUsElement.classList.add('show');
        }
      }

      modal.innerHTML = modal.innerHTML.replace('{text}', messageBody);
    } else {
      modal.innerHTML = modal.innerHTML.replace('{text}', '');
    }
    return modal;
  }

  /**
   * Opens a success modal after successfully cloning process.
   *
   * @param {Object} params - The parameters for initializing the success modal.
   * @param {boolean} [showLogs=false] - Whether to display process logs in the modal.
   * @param {string|null} [siteUrl=null] - The URL of the staging site, if applicable.
   * @return {void}
   */
  openSuccessModal(params, showLogs = false, siteUrl = null) {
    this.stagingSiteUrl = siteUrl;
    this.showProcessLogs = showLogs;
    this.initializeSuccessModal(params);
  }
}
